import React, { useRef, useState } from "react";
import { Paperclip, Send } from "lucide-react";

type MessageInputProps = {
  placeholder?: string;
  disabled?: boolean;
  onSend: (text: string) => void;
  onAttachFiles?: (files: FileList) => void; // still uses FileList, but images only
};

export const MessageInput: React.FC<MessageInputProps> = ({
  placeholder = "Type a Message",
  disabled = false,
  onSend,
  onAttachFiles,
}) => {
  const [text, setText] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  const triggerFile = () => fileInputRef.current?.click();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || !files.length) return;

    // extra safety: pass only images onward
    const imageFiles = Array.from(files).filter((f) =>
      (f.type || "").startsWith("image/")
    );

    if (imageFiles.length && onAttachFiles) {
      // Build a new FileList-like object for consumers
      const dt = new DataTransfer();
      imageFiles.forEach((f) => dt.items.add(f));
      onAttachFiles(dt.files);
    }

    // reset so selecting the same file again will still fire change
    e.target.value = "";
  };

  const submit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmed = text.trim();
    if (!trimmed || disabled) return;
    onSend(trimmed);
    setText("");
  };

  return (
    <form onSubmit={submit} className="w-full">
      <div className="flex items-center gap-3">
        {/* Input pill */}
        <div
          className={[
            "flex w-full items-center gap-3 rounded-2xl border border-gray-200 bg-white",
            "px-4 py-3 shadow-sm",
            disabled ? "opacity-70" : "",
          ].join(" ")}
        >
          <button
            type="button"
            onClick={triggerFile}
            disabled={disabled}
            aria-label="Attach images"
            className="grid h-6 w-6 place-items-center text-gray-500 hover:text-gray-700"
          >
            <Paperclip className="h-5 w-5" />
          </button>

          <input
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={placeholder}
            disabled={disabled}
            className="peer w-full border-none bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
          />

          {/* hidden image-only file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileChange}
            className="hidden"
          />
        </div>

        {/* Send button */}
        <button
          type="submit"
          disabled={disabled || !text.trim()}
          aria-label="Send message"
          className={[
            "grid h-12 w-12 shrink-0 place-items-center rounded-2xl text-white shadow-md transition",
            "bg-rose-600 hover:bg-rose-700 disabled:bg-rose-300",
          ].join(" ")}
        >
          <Send className="h-5 w-5" />
        </button>
      </div>
    </form>
  );
};
